// ==============================================
// Trainers Listing & Booking Page
// ==============================================

import React, { useState, useEffect } from 'react';
import { trainersAPI, citiesAPI } from '../services/api';
import { TrainerProfile, City } from '../types';

const Trainers: React.FC = () => {
  const [trainers, setTrainers] = useState<TrainerProfile[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Booking modal state
  const [selectedTrainer, setSelectedTrainer] = useState<TrainerProfile | null>(null);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingDuration, setBookingDuration] = useState('60');
  const [bookingNotes, setBookingNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load cities
      const citiesResponse = await citiesAPI.getAll();
      if (citiesResponse.success && citiesResponse.data) {
        setCities(citiesResponse.data.cities || []);
      }

      // Load trainers
      const params = selectedCity ? { city_id: parseInt(selectedCity) } : undefined;
      const trainersResponse = await trainersAPI.getAll(params);
      
      if (trainersResponse.success && trainersResponse.data) {
        setTrainers(trainersResponse.data.trainers || []);
      } else {
        setError(trainersResponse.message || 'Failed to load trainers');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedCity]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTrainers = trainers.filter(trainer => {
    const matchesSearch = 
      trainer.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trainer.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trainer.bio?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTrainer || !bookingDate || !bookingTime) {
      alert('Please fill in all required fields');
      return;
    }

    const bookingData = {
      trainer_id: selectedTrainer.id,
      session_date: bookingDate,
      start_time: bookingTime,
      duration_minutes: parseInt(bookingDuration),
      notes: bookingNotes || undefined,
    };

    try {
      setSubmitting(true);
      const response = await trainersAPI.createBooking(bookingData);
      
      if (response.success) {
        alert('Booking request sent successfully!');
        setSelectedTrainer(null);
        setBookingDate('');
        setBookingTime('');
        setBookingDuration('60');
        setBookingNotes('');
      } else {
        alert(response.message || 'Booking failed');
      }
    } catch (err: any) {
      alert(err.message || 'An error occurred while booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="container page-inner">
        <div className="page-header">
          <h2>Găsește un antrenor</h2>
          <p>Rezervă-ți locul cu cei mai profesioniști antrenori.</p>
        </div>

        <div className="filter-bar">
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search trainers by name…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                <option value="">Toate orașele</option>
                {cities.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
              </select>
                </div>
          </div>
        </div>

        {loading && (
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
            <span>Se încarcă…</span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <>
            <p className="result-count">{filteredTrainers.length} trainer{filteredTrainers.length !== 1 ? 's' : ''} found</p>

            {filteredTrainers.length === 0 ? (
              <div className="tt-empty">
                <div className="tt-empty-icon">👨‍🏫</div>
                <h5>Niciun antrenor găsit.</h5>
                <p>Încearcă mai târziu.</p>
              </div>
            ) : (
              <div className="row g-4">
                {filteredTrainers.map(trainer => {
                  const initials = `${trainer.user?.first_name?.[0] ?? ''}${trainer.user?.last_name?.[0] ?? ''}`.toUpperCase();
                  return (
                    <div key={trainer.id} className="col-md-6 col-lg-4">
                      <div className="entity-card">
                        <div className="entity-card-body">
                          <div className="d-flex gap-3 align-items-center mb-3">
                            <div className="avatar">{initials}</div>
                            <div>
                              <div className="fw-semibold">{trainer.user?.first_name} {trainer.user?.last_name}</div>
                              <span className={`badge ${trainer.status === 'active' ? 'status-active' : 'status-inactive'}`}>{trainer.status}</span>
                            </div>
                          </div>
                          {trainer.city && (
                            <p style={{ fontSize: '.82rem', color: 'var(--tt-text-muted)' }} className="mb-1">📍 {trainer.city.name}</p>
                          )}
                          <div className="d-flex gap-3 mb-2" style={{ fontSize: '.82rem', color: 'var(--tt-text-2)' }}>
                            {trainer.experience_years > 0 && <span>⏱ {trainer.experience_years} yrs exp</span>}
                            {trainer.hourly_rate && <span>💰 {trainer.hourly_rate} {trainer.currency}/h</span>}
                          </div>
                          {trainer.bio && (
                            <p style={{ fontSize: '.85rem', color: 'var(--tt-text-muted)' }} className="mb-0">
                              {trainer.bio.length > 110 ? `${trainer.bio.substring(0, 110)}…` : trainer.bio}
                            </p>
                          )}
                          {trainer.certifications && (
                            <p style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)' }} className="mt-2 mb-0">
                              🏅 {trainer.certifications}
                            </p>
                          )}
                        </div>
                        <div className="entity-card-footer">
                          <button
                            className="btn btn-primary btn-sm w-100"
                            onClick={() => setSelectedTrainer(trainer)}
                            disabled={trainer.status !== 'active'}
                          >
                            Book Session
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      {/* Booking Modal */}
      {selectedTrainer && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Book Session with {selectedTrainer.user?.first_name} {selectedTrainer.user?.last_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedTrainer(null)}
                  disabled={submitting}
                ></button>
              </div>
              <form onSubmit={handleBookingSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Time *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={bookingTime}
                      onChange={(e) => setBookingTime(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Duration *</label>
                    <select
                      className="form-select"
                      value={bookingDuration}
                      onChange={(e) => setBookingDuration(e.target.value)}
                      required
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes (optional)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      placeholder="Any specific goals or areas you want to focus on..."
                    ></textarea>
                  </div>

                  {selectedTrainer.hourly_rate && (
                    <div className="alert alert-info">
                      <strong>Estimated Cost:</strong> {
                        (parseFloat(selectedTrainer.hourly_rate.toString()) * (parseInt(bookingDuration) / 60)).toFixed(2)
                      } {selectedTrainer.currency}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedTrainer(null)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Booking...' : 'Confirm Booking'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Trainers;
